import type { FC, SyntheticEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";

import type { Translate } from "@similarweb/i18n-provider";
import { Crown, SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import type { Track } from "@similarweb/tracking-provider";
import type { ICountryDropdownItem } from "@similarweb/ui-components";
import { Badge, CountryDropdownItem, Dropdown, DropdownButton } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { americaStatesHandle, SortingOption } from "app/components/filters-bar/country-filter/CountryFilterCommon";
import type { ESortingType } from "app/components/filters-bar/country-filter/CountryFilterTypes";
import { FILTER_CHANGED_NOTIFICATION_WIDTH } from "app/components/filters-bar/FiltersBase";
import type { IFilterBaseProps } from "app/components/filters-bar/FiltersBase";
import { translate } from "app/filters/i18nFilter";
import { useCountryFilterLockedClick } from "app/marketing/hooks/useCountryFilterLockedClick";
import { numFilter } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import {
    isDMINoTouchOrTrialOrAfterTrial,
    isTraditionalTrialOrAfterTrialUser,
    isNoTouch,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import FilterChangedNotification from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltip";
import { EFilterChangedTooltipTypes } from "pro-features/components/tooltips/src/filterChangedTooltip/FilterChangedTooltipTypes";
import WithTrack from "pro-features/components/WithTrack/src/WithTrack";

import TrialHook from "../../React/TrialHook/TrialHook";

import { BetaWrapper } from "./BetaWrapper";
import { CountryDetailsWrapper, CountryFilterStyledWrapper, NotificationWrapper } from "./StyledComponent";

interface ICountryFilterProps extends IFilterBaseProps {
    selectedDomain?: string;
    selectedDuration?: string;
    selectedCountry?: string;
    sortChange?: (sortingType: ESortingType) => void;
    countryFilterSortingText?: string;
    isCountryFilterAllowData?: boolean;
    isUSStatesSupported?: boolean;
    countryFilterInitialSortingType?: ESortingType;
    availableCountries: ICountryDropdownItem[];
    data?: number;
    changeCountry?: (itemId: number) => void;
    selectedCountryIds: Record<string, boolean>;
    dropdownPopupWidth?: number | string;
    cssClassContainer?: string;
    renderButtonComponent?: (selectedCountry: object) => React.ReactNode;
    showUpgradeHook?: boolean;
    dropdownPopupHeight?: number;
    getFilterNotificationComponent?: React.FunctionComponent;
    betaIds?: number[];
    searchPlaceHolder?: string;
    /**
     * Opt-in escape hatch for opening the dropdown from outside the filters bar.
     * The underlying Dropdown is uncontrolled, so the popup is opened imperatively
     * whenever this flips to true. Treated as a fire-and-forget pulse: CountryFilter
     * calls onOpenRequestConsumed after attempting to open (including when the attempt
     * is skipped), so a blocked open cannot wedge the trigger.
     */
    isOpenRequested?: boolean;
    onOpenRequestConsumed?: VoidFunction;
}

interface ICountryDropdownItemProxyProps extends ICountryDropdownItem {
    onLockedItemClick?: (countryCode: string) => void;
    onCrownLockedClick?: () => void;
    hasCountryFilterInAvailableTypes: boolean;
}

const CountryDropdownItemProxy = ({
    permitted,
    onClick,
    text,
    data,
    onLockedItemClick,
    onCrownLockedClick,
    hasCountryFilterInAvailableTypes = false,
    ...props
}: ICountryDropdownItemProxyProps) => {
    const hasLockedRedirect = !!onLockedItemClick && !permitted;

    const onClickClb = useCallback(
        (event: SyntheticEvent<HTMLElement>, track: Track) => {
            const Action = {
                open: "Open",
                crown: "Crown",
                unlock: "Unlock",
            } as const;
            type ActionType = (typeof Action)[keyof typeof Action];

            const trackClick = (actionType: ActionType) =>
                track("Drop Down", "click", `Header/Country Filter/${text} - ${actionType}`);

            if (!permitted && !hasCountryFilterInAvailableTypes && onCrownLockedClick) {
                trackClick(Action.crown);
                onCrownLockedClick();
                return;
            }

            if (hasLockedRedirect) {
                trackClick(Action.unlock);
                // @ts-ignore FIXME: strict migration - 'code' is possibly 'undefined' — hasLockedRedirect ensures code exists at runtime
                onLockedItemClick(props.code);
                return;
            }

            if (!permitted && onCrownLockedClick) {
                trackClick(Action.crown);
                onCrownLockedClick();
                return;
            }

            if (permitted) {
                trackClick(Action.open);
                onClick?.(event);
            }
        },
        [
            permitted,
            onCrownLockedClick,
            hasLockedRedirect,
            text,
            onLockedItemClick,
            props.code,
            onClick,
            hasCountryFilterInAvailableTypes,
        ],
    );

    const resolvedData = useMemo(() => {
        if (!permitted && !hasCountryFilterInAvailableTypes) {
            return (
                <Crown
                    size="xs"
                    color={colorsPalettes.indigo[300]}
                />
            );
        }

        if (hasLockedRedirect) {
            return isDMINoTouchOrTrialOrAfterTrial(swSettings) ? (
                <Badge bgColor={colorsPalettes.blue[100]}>
                    <Text
                        fontVariant={"caption2"}
                        color={colorsPalettes.blue[400]}
                    >
                        {translate("pro.side.bar.unlock.badge.component.test")}
                    </Text>
                </Badge>
            ) : null;
        }
        if (!permitted) {
            return (
                <Crown
                    size="xs"
                    color={colorsPalettes.indigo[300]}
                />
            );
        }
        if (typeof data === "string" || typeof data === "number") {
            return numFilter(data);
        }
        return data;
    }, [hasLockedRedirect, permitted, data, hasCountryFilterInAvailableTypes]);

    return (
        <WithTrack>
            {(track) => (
                <CountryDropdownItem
                    {...props}
                    isLastPermitted={props.isLastPermitted}
                    onClick={(e) => onClickClb(e, track)}
                    text={text}
                    isClickAllowed={true}
                    data={resolvedData}
                />
            )}
        </WithTrack>
    );
};

interface ICreateDropDownActionButtonData {
    translate: Translate;
    id: number;
    text: string;
    width: string | number;
    height: string | number;
    disabled: boolean;
    parentId?: string | number;
    presentNotificationIndicator?: boolean;
    presentNotificationTooltipKey?: string;
    getFilterNotificationComponent?: FC;
}

function createDropDownActionButton(data: ICreateDropDownActionButtonData) {
    const {
        translate,
        id,
        text,
        width,
        height,
        disabled,
        parentId,
        presentNotificationIndicator,
        presentNotificationTooltipKey,
        getFilterNotificationComponent,
    } = data;
    const countryId = parentId ? parentId : id;
    // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string' — component handles undefined case
    const body = translate(presentNotificationTooltipKey);
    const ChangedNotificationToRender =
        typeof getFilterNotificationComponent === "function" ? (
            // @ts-expect-error error TS2322: Type 'FunctionComponent<{}>' is not assignable to type 'ReactNode'.
            <NotificationWrapper>{getFilterNotificationComponent({})}</NotificationWrapper>
        ) : (
            <FilterChangedNotification
                width={FILTER_CHANGED_NOTIFICATION_WIDTH}
                tooltipBody={body}
                filterType={EFilterChangedTooltipTypes.COUNTRY_PICKER}
            />
        );

    return (
        <CountryFilterStyledWrapper key="button">
            <DropdownButton
                key={0}
                width={width}
                height={height}
                cssClass="DropdownButton--filtersBarDropdownButton DropdownButton--filtersBarDropdownButton--country"
                disabled={disabled}
            >
                <div className="CountryFilter-dropdownButton">
                    <CountryDetailsWrapper>
                        <SWReactCountryIcons
                            countryCode={Number(countryId)}
                            size="sm"
                            className="without-hover-fill"
                        />
                        <div className={`CountryFilter-dropdownButton-text ${id ? "has-value" : ""}`}>{text}</div>
                    </CountryDetailsWrapper>
                    {presentNotificationIndicator ? ChangedNotificationToRender : null}
                </div>
            </DropdownButton>
        </CountryFilterStyledWrapper>
    );
}

export const CountryFilter: FC<ICountryFilterProps> = (props) => {
    const dropdownRef = useRef<Dropdown<unknown>>(null);

    const { openContactSales, lockedItemCallbacks } = useCountryFilterLockedClick({ dropdownRef });

    const {
        availableCountries = [],
        selectedCountry,
        selectedDomain,
        selectedDuration,
        countryFilterInitialSortingType,
        height = 70,
        width = 198,
        disabled = false,
        onToggle = () => void 0,
        dropdownPopupPlacement = "bottom-left",
        dropdownPopupWidth = 320,
        cssClassContainer = "",
        appendTo = "body",
        showUpgradeHook: isShowUpgradeHook = isNoTouch(swSettings),
        dropdownPopupHeight = 240,
        countryFilterSortingText = translate("country.filter.sorting.by.default"),
        isCountryFilterAllowData = false,
        selectedCountryIds,
        changeCountry,
        sortChange,
        betaIds,
        isUSStatesSupported = false,
        presentNotificationIndicator = false,
        presentNotificationTooltipKey,
        getFilterNotificationComponent,
        renderButtonComponent,
        itemWrapper,
        isOpenRequested = false,
        onOpenRequestConsumed,
    } = props;
    // Handle the case where we're moving to a state with no country filter
    // e.g. geography and technographics
    const [sortingType, setSortingType] = useState(countryFilterInitialSortingType);

    useEffect(() => {
        setSortingType(countryFilterInitialSortingType);
    }, [countryFilterInitialSortingType, selectedDuration, selectedCountry, selectedDomain]);

    useEffect(() => {
        if (!isOpenRequested) {
            return;
        }

        if (!disabled) {
            // @ts-expect-error popupClickRef is private on Dropdown, which exposes no controlled open API
            dropdownRef.current?.popupClickRef?.openPopup();
        }

        onOpenRequestConsumed?.();
    }, [isOpenRequested, disabled, onOpenRequestConsumed]);

    const allCountriesAreOpen = availableCountries.every((country) => country.permitted);
    const onChangeSortingType = (newType: ESortingType) => {
        setSortingType(newType);
    };

    const showUpgradeHook = isTraditionalTrialOrAfterTrialUser(swSettings) || isShowUpgradeHook;

    const footerComp = useMemo(() => {
        if (showUpgradeHook) {
            return (
                <TrialHook
                    text="trial_hook.country.text"
                    buttonText="trial_hook.country.button.text.v2"
                    label="Country Filter"
                    onCtaClick={openContactSales}
                    ctaIconName="sparkles"
                />
            );
        }

        return null;
    }, [showUpgradeHook, openContactSales]);

    const optionalSorting = isCountryFilterAllowData && (
        <SortingOption
            sortingByDataTxt={countryFilterSortingText}
            // @ts-ignore FIXME: strict migration - Type '((sortingType: ESortingType) => void) | undefined' is not assignable — optional prop pattern
            sortChange={sortChange}
            // @ts-ignore FIXME: strict migration - Type 'ESortingType | undefined' is not assignable to type 'number' — undefined case handled by state initialization
            sortingType={sortingType}
            onChangeSortingType={onChangeSortingType}
        />
    );

    if (
        selectedCountryIds === null ||
        typeof selectedCountryIds !== "object" ||
        typeof selectedCountryIds === "undefined"
    ) {
        return null;
    }

    const selectedId = parseInt(Object.keys(selectedCountryIds)[0], 10);
    const selectedItem = availableCountries.find((country) => country.id === selectedId);

    const selectedText = selectedItem?.text;
    const button =
        typeof renderButtonComponent === "function"
            ? // @ts-ignore FIXME: strict migration - Argument of type 'ICountryDropdownItem | null' is not assignable to parameter of type 'object' — renderButtonComponent accepts country object
              renderButtonComponent(selectedItem)
            : createDropDownActionButton({
                  translate,
                  id: selectedId,
                  // @ts-ignore FIXME: strict migration - Type 'string | undefined' is not assignable to type 'string' — selectedItem fallback ensures text exists
                  text: selectedText,
                  width,
                  height,
                  disabled,
                  parentId: selectedItem?.parent,
                  presentNotificationIndicator,
                  presentNotificationTooltipKey,
                  getFilterNotificationComponent,
              });

    let nonSelectedCountries: ICountryDropdownItem[] = [];
    if (selectedItem && isUSStatesSupported && (selectedItem.id === 840 || selectedItem.parent === 840)) {
        nonSelectedCountries = americaStatesHandle(availableCountries, selectedItem);
    } else {
        nonSelectedCountries = availableCountries.filter((country) => country.id !== selectedItem?.id);
    }

    if (selectedItem && allCountriesAreOpen) {
        //separateLine logic
        selectedItem.isLastPermitted = selectedItem.id === availableCountries[0].id;
    } else if (selectedItem) {
        if (selectedItem.isLastPermitted) {
            selectedItem.isLastPermitted = false;
            const index = availableCountries.findIndex((country) => !country.permitted);
            if (nonSelectedCountries[index - 2]) {
                nonSelectedCountries[index - 2].isLastPermitted = true;
            }
            //this logic is only if selected country is the last country in the permitted country list (after sorting)
            //so we are taking the unpermitted list and we want to add the line to (index - 2) - last one after the last permitted.
        }
    }

    const contents = [button, selectedItem, ...nonSelectedCountries];

    const itemsComponent = (itemProps: ICountryDropdownItem) => {
        const isBeta = betaIds ? betaIds.includes(Number(itemProps?.id)) : false;

        if (!isBeta) {
            return (
                <CountryDropdownItemProxy
                    {...itemProps}
                    {...lockedItemCallbacks}
                />
            );
        }

        return (
            <BetaWrapper isBeta={isBeta}>
                <CountryDropdownItemProxy
                    {...itemProps}
                    {...lockedItemCallbacks}
                />
            </BetaWrapper>
        );
    };

    return (
        <Dropdown
            ref={dropdownRef}
            headerComponent={optionalSorting}
            disabled={disabled}
            appendTo={appendTo}
            onClick={changeCountry}
            onToggle={onToggle}
            selectedIds={selectedCountryIds}
            itemsComponent={itemsComponent}
            width={dropdownPopupWidth}
            dropdownPopupHeight={dropdownPopupHeight}
            hasSearch
            closeOnItemClick
            shouldScrollToSelected
            dropdownPopupMinScrollHeight={48}
            cssClassContainer={classNames(
                "DropdownContent-container",
                "FiltersBarDropdown",
                cssClassContainer,
                showUpgradeHook && "FiltersBarDropdown--trial",
            )}
            itemWrapper={itemWrapper}
            dropdownPopupPlacement={dropdownPopupPlacement}
            searchPlaceHolder={translate(props?.searchPlaceHolder || "common.countryFilter.placeholder.text")}
            footerComponent={() => footerComp}
        >
            {contents}
        </Dropdown>
    );
};

CountryFilter.displayName = "CountryFilter";
